import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, AlertCircle, CheckCircle2, Loader2, KeyRound } from "lucide-react";
import { Modal } from "../ui";
import { supabase } from "../../supabaseClient";
import { getBranding } from "../../config/branding";
import { AuthBrandLogo } from "./AuthBrandLogo";

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ForgotPasswordModal({ isOpen, onClose }: ForgotPasswordModalProps) {
  const { t } = useTranslation();
  const branding = getBranding();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      if (!email.trim()) {
        setError(t("auth.emailRequired"));
        return;
      }

      // resetPasswordForEmail : redirection vers la racine du site (window.location.origin)
      // pour que l'utilisateur revienne sur l'app (Vercel ou local) pour changer son mot de passe.
      // Supabase envoie l'email via le SMTP configuré (Resend en production).
      const siteRoot =
        typeof window !== "undefined" ? `${window.location.origin}/` : "";
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: siteRoot,
      });

      if (resetError) {
        setError(resetError.message);
        return;
      }

      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError("");
    setSuccess(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="flex flex-col items-center text-center mb-8">
        <div className="mb-4 flex flex-col items-center gap-2">
          <AuthBrandLogo branding={branding} size="modal" />
          {branding.logoDisplay !== "full" && (
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 tracking-wide">
              {branding.brandName}
            </p>
          )}
        </div>
        <div className="w-16 h-16 bg-indigo-100 dark:bg-slate-700 border border-indigo-200 dark:border-white/10 rounded-full flex items-center justify-center mb-4 text-indigo-600 dark:text-indigo-400">
          <KeyRound size={28} />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
          {t("auth.forgotPasswordTitle")}
        </h2>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">
          {t("auth.forgotPasswordDesc")}
        </p>
      </div>

      {!success ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="group">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-white/10 rounded-2xl outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-indigo-500"
                placeholder={t("auth.emailPlaceholder")}
                autoFocus
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center justify-center gap-2 text-red-500 dark:text-red-400 text-xs font-medium py-1 animate-pulse">
              <AlertCircle className="w-3 h-3 shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white font-medium rounded-2xl shadow-lg shadow-indigo-500/30 hover:opacity-95 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              t("auth.sendLink")
            )}
          </button>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="text-xs text-gray-500 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
            >
              {t("auth.backToLogin")}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-6">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-6">
            <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400 mb-4">
              <CheckCircle2 className="w-6 h-6" />
              <span className="font-semibold">{t("auth.emailSentTitle")}</span>
            </div>
            
            <div className="space-y-3 text-sm text-gray-700 dark:text-slate-300">
              <p>
                {t("auth.resetEmailSentTo")}
              </p>
              <p className="font-medium text-gray-900 dark:text-white">
                {email}
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {t("auth.checkInbox")}
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400 italic">
                💡 {t("auth.checkSpam")}
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium rounded-2xl shadow-lg shadow-blue-500/30 hover:opacity-95 transition-opacity"
          >
            {t("auth.backToLogin")}
          </button>
        </div>
      )}
    </Modal>
  );
}
